import { useCryptStore } from "@/store/crypt";
import { type TypedDocumentNode } from "@graphql-typed-document-node/core";
import {
  useMutation as useMutation_,
  type UseMutationResult,
  useQuery as useQuery_,
  type UseQueryResult,
} from "@tanstack/react-query";
import { Buffer } from "buffer";
import { type ExecutionResult, print } from "graphql";
import React from "react";
import { graphqlApi } from "./api";

async function customFetcher<TResult, TVariables>(
  document: TypedDocumentNode<TResult, TVariables>,
  variables?: TVariables,
): Promise<TResult> {
  const state = useCryptStore.getState();
  const encryped_data = await state.aes_gcm_encrypt(
    Buffer.from(
      JSON.stringify({
        query: print(document),
        variables,
      }),
    ).buffer,
  );
  const response = await graphqlApi.post<ExecutionResult<TResult>>(
    "/",
    encryped_data,
  );
  if (response.status !== 200) {
    throw new Error(
      `Failed to fetch: ${response.statusText}. Body: ${response.data}`,
    );
  }
  return response.data.data as TResult;
}

const MAX_LIMIT = 100;

export function useQuery<TResult, TVariables extends Record<string, any>>(
  document: TypedDocumentNode<TResult, TVariables>,
  variables: TVariables,
): UseQueryResult<TResult> {
  // Clamp limit to MAX_LIMIT if it exists in variables
  const processedVariables = { ...variables } as Record<string, any>;
  if (
    typeof processedVariables.limit === "number" &&
    processedVariables.limit > MAX_LIMIT
  ) {
    processedVariables.limit = MAX_LIMIT;
  }

  return useQuery_({
    queryKey: [(document.definitions[0] as any).name.value, processedVariables],
    queryFn: () =>
      customFetcher<TResult, TVariables>(
        document,
        processedVariables as TVariables,
      ),
  });
}
export function useMutation<TResult, TVariables>(
  document: TypedDocumentNode<TResult, TVariables>,
): UseMutationResult<TResult, Error, TVariables> {
  return useMutation_({
    mutationFn: (variables: TVariables) =>
      customFetcher<TResult, TVariables>(document, variables),
  });
}

/**
 * Hook for handling infinite scroll in Select components
 * Automatically handles pagination and loading more items
 */
export interface UseSelectOptionsConfig<TResult, TVariables> {
  document: TypedDocumentNode<TResult, TVariables>;
  initialVariables: TVariables;
  getNodes: (result: TResult) => Array<{ label: string; value: any }>;
  pageSize?: number;
}

export function useSelectOptions<
  TResult,
  TVariables extends Record<string, any>,
>(config: UseSelectOptionsConfig<TResult, TVariables>) {
  const { document, initialVariables, getNodes, pageSize = 50 } = config;
  const [page, setPage] = React.useState(0);
  const [options, setOptions] = React.useState<
    Array<{ label: string; value: any }>
  >([]);
  const [hasMore, setHasMore] = React.useState(true);

  const { data, isLoading, error } = useQuery(document, {
    ...initialVariables,
    limit: Math.min(pageSize, MAX_LIMIT),
    page,
  } as TVariables);

  React.useEffect(() => {
    if (data) {
      const newNodes = getNodes(data);
      if (page === 0) {
        setOptions(newNodes);
      } else {
        setOptions((prev) => [...prev, ...newNodes]);
      }
      // Check if there are more items to load
      setHasMore(newNodes.length > 0);
    }
  }, [data, page, getNodes]);

  const handleScroll = (event: any) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (
      scrollHeight - scrollTop <= clientHeight + 50 &&
      hasMore &&
      !isLoading
    ) {
      setPage((prev) => prev + 1);
    }
  };

  const reset = () => {
    setPage(0);
    setOptions([]);
    setHasMore(true);
  };

  return {
    options,
    isLoading,
    error,
    hasMore,
    handleScroll,
    reset,
  };
}
